/**
 * 단축키 카탈로그 (Phase 3)
 *
 * ⚠️ 이 파일은 **참조용 목록**이다. 실제 바인딩은 아래 세 곳에 흩어져 있고,
 *    여기 값을 바꿔도 동작은 바뀌지 않는다. 코드를 고치면 이 목록도 함께 고쳐야 한다.
 *
 *   1) components/SQLEditor.js — CodeMirror `keymap.of([...])` (에디터 포커스 시)
 *   2) App.js                  — 전역 `keydown` 핸들러 (앱 어디서나)
 *   3) components/Header.js    — 메뉴의 `shortcut:` 표시 (표시 전용)
 *
 * 편집 가능한 키맵으로 가려면 위 세 곳을 이 목록 기반으로 재작성해야 한다(별도 작업).
 * 지금은 "무엇이 어디에 걸려 있는지" 를 한눈에 보고 충돌을 찾는 용도다.
 */

// [i18n] 모듈 스코프라 t() 를 못 쓴다. group/action/note/scope 는 **키**만 담고
// 화면에서 t() 로 번역한다. key(실제 키 조합)와 source(파일 경로)는 번역하지 않는다 —
// source 는 화면에 그리지 않는 개발자용 출처 정보다.
// key 가 실제 조합이 아닌 두 항목((none)/Ctrl+Wheel)만 keyLabelKey 로 표시 문구를 준다.

/** scope: 'global' = 앱 전역, 'editor' = SQL 에디터 포커스 시 */
export const KEY_BINDINGS = [
  // ── 파일 ──
  { groupKey: 'settings:keys.group.file', key: 'Alt+N',        actionKey: 'settings:keys.action.newFile',           scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.file', key: 'Ctrl+O',       actionKey: 'settings:keys.action.open',              scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.file', key: 'Ctrl+S',       actionKey: 'settings:keys.action.save',              scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.file', key: 'Ctrl+Shift+O', actionKey: 'settings:keys.action.openProject',     scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.file', key: 'Ctrl+Shift+S', actionKey: 'settings:keys.action.saveProject',     scope: 'global', source: 'App.js' },

  // ── 편집 ──
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+Z',       actionKey: 'settings:keys.action.undo',         scope: 'editor', source: 'CodeMirror 기본' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+Y',       actionKey: 'settings:keys.action.redo',         scope: 'editor', source: 'CodeMirror 기본' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+C',       actionKey: 'settings:keys.action.copy',              scope: 'editor', source: '브라우저 기본' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+X',       actionKey: 'settings:keys.action.cut',          scope: 'editor', source: '브라우저 기본' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+V',       actionKey: 'settings:keys.action.paste',          scope: 'editor', source: '브라우저 기본' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+F',       actionKey: 'settings:keys.action.findInEditor',    scope: 'editor', source: 'SQLEditor.js -> App.js toolbar' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+H',       actionKey: 'settings:keys.action.replaceInEditor', scope: 'editor', source: 'SQLEditor.js -> @codemirror/search panel' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+Shift+F', actionKey: 'settings:keys.action.globalSearch',         scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.edit', key: 'F3',           actionKey: 'settings:keys.action.findNext',         scope: 'editor', source: '@codemirror/search searchKeymap' },
  { groupKey: 'settings:keys.group.edit', key: 'Shift+F3',     actionKey: 'settings:keys.action.findPrevious',         scope: 'editor', source: '@codemirror/search searchKeymap' },
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+G',       actionKey: 'settings:keys.action.findNextSame',  scope: 'editor', source: '@codemirror/search searchKeymap' },
  // 주석 토글 — 메뉴 작성 › 에도 단축키로 표시된다(설정 화면 목록에서 빠져 있었다, 2026-10-29)
  { groupKey: 'settings:keys.group.edit', key: 'Ctrl+/',       actionKey: 'menu:edit.toggleComment', scope: 'editor', source: '@codemirror/commands toggleComment' },
  { groupKey: 'settings:keys.group.edit', key: 'Tab',          actionKey: 'settings:keys.action.acceptCompletionIndent', scope: 'editor', source: 'SQLEditor.js' },
  { groupKey: 'settings:keys.group.edit', key: 'Shift+Tab',    actionKey: 'settings:keys.action.outdentLines', scope: 'editor', source: 'SQLEditor.js' },

  // ── 실행 ──
  { groupKey: 'settings:keys.group.run', key: 'Ctrl+Enter',   actionKey: 'settings:keys.action.runCurrentStatement',    scope: 'editor', source: 'SQLEditor.js' },
  { groupKey: 'settings:keys.group.run', key: 'F5',           actionKey: 'settings:keys.action.runAll',         scope: 'global', source: 'App.js' },
  { groupKey: 'settings:keys.group.run', key: '(none)', keyLabelKey: 'settings:keys.keyLabel.none',       actionKey: 'settings:keys.action.viewExecutionHistory',     scope: 'menu',   source: 'Header.js — 메뉴에서 실행' },
  { groupKey: 'settings:keys.group.run', key: 'Ctrl+E',       actionKey: 'settings:keys.action.viewExecutionPlan',     scope: 'both',   source: 'SQLEditor.js + App.js' },
  { groupKey: 'settings:keys.group.run', key: 'F4',           actionKey: 'settings:keys.action.viewSchemaInformation',  scope: 'editor', source: 'SQLEditor.js',
    noteKey: 'settings:keys.note.f4' },

  // ── 보기 ──
  { groupKey: 'settings:keys.group.view', key: 'F2',           actionKey: 'settings:keys.action.columnCommentTooltip',    scope: 'editor', source: 'SQLEditor.js' },
  // 화면 확대/축소 — 앱 전체 한 배율(utils/appZoom). 웹은 브라우저 확대가 같은 키로 동작한다
  { groupKey: 'settings:keys.group.view', key: 'Ctrl+Wheel', keyLabelKey: 'settings:keys.keyLabel.ctrlWheel',      actionKey: 'settings:keys.action.appZoom',  scope: 'global', source: 'utils/appZoom.js' },
  { groupKey: 'settings:keys.group.view', key: 'Ctrl+=',       actionKey: 'settings:keys.action.zoomIn',     scope: 'global', source: 'utils/appZoom.js' },
  { groupKey: 'settings:keys.group.view', key: 'Ctrl+-',       actionKey: 'settings:keys.action.zoomOut',    scope: 'global', source: 'utils/appZoom.js' },
  { groupKey: 'settings:keys.group.view', key: 'Ctrl+0',       actionKey: 'settings:keys.action.zoomReset',  scope: 'global', source: 'utils/appZoom.js' },
  // 작업 탭 오가기 — 본 순서의 기록을 따라(브라우저의 뒤로/앞으로처럼)
  { groupKey: 'settings:keys.group.view', key: 'Alt+Left',     actionKey: 'settings:keys.action.prevWindow', scope: 'global', source: 'components/workspace/WorkspaceDock.js' },
  { groupKey: 'settings:keys.group.view', key: 'Alt+Right',    actionKey: 'settings:keys.action.nextWindow', scope: 'global', source: 'components/workspace/WorkspaceDock.js' },
];

/**
 * 같은 키가 둘 이상 걸린 항목을 찾는다.
 * scope 가 달라도(에디터 vs 전역) 같은 키면 에디터에서 어느 쪽이 먹는지 헷갈리므로 함께 알린다.
 * @returns {Object<string, Array>} key → 항목 배열 (2개 이상인 것만)
 */
export function findConflicts(list = KEY_BINDINGS) {
  const byKey = {};
  for (const b of list) {
    const k = b.key.toUpperCase();
    (byKey[k] = byKey[k] || []).push(b);
  }
  const out = {};
  for (const [k, arr] of Object.entries(byKey)) {
    if (arr.length > 1) out[k] = arr;
  }
  return out;
}

/** 그룹 순서를 유지한 채 그룹별로 묶는다. */
export function groupBindings(list = KEY_BINDINGS) {
  const order = [];
  const map = {};
  for (const b of list) {
    if (!map[b.groupKey]) { map[b.groupKey] = []; order.push(b.groupKey); }
    map[b.groupKey].push(b);
  }
  return order.map(g => ({ group: g, items: map[g] }));
}

export const SCOPE_LABEL_KEY = {
  global: 'settings:keys.scope.global',
  editor: 'settings:keys.scope.editor',
  both: 'settings:keys.scope.both',
  menu: 'settings:keys.scope.menu',
};
